import { axiosOtherTrackerInstance } from "../configs/AxiosInstance";
import { formatLocalDate, parseLocalDate } from "../utils/formatLocalDate";

/*
 * Water / steps / sleep summaries come from other-trackers'
 * fetch-daily-*-tracking-summary routes, which return one row per tracked day
 * for `client_id`. The charts were built for a richer legacy payload, so each
 * fetcher maps rows to { date, value, recommended, percentage, status } and
 * derives average / streak client-side.
 */

const RANGE_DAYS = { D: 1, W: 7, M: 30 };

const rangeWindow = (range, currentDate) => {
  const end = parseLocalDate(currentDate);
  const start = new Date(end);
  start.setDate(end.getDate() - ((RANGE_DAYS[range] || 1) - 1));
  return { start: formatLocalDate(start), end: formatLocalDate(end) };
};

const fetchDailyTrackingSummary = async (get, path, profileId, range, currentDate, eventTypeId) => {
  const { start, end } = rangeWindow(range, currentDate);
  const response = await get(
    `/${path}?event_type_id=${eventTypeId}&client_id=${profileId}` +
      `&start_date=${start}T00:00:00&end_date=${end}T23:59:59`,
    { instance: axiosOtherTrackerInstance }
  );
  return Array.isArray(response?.data) ? response.data : null;
};

const round1 = (n) => Math.round(n * 10) / 10;

// Below 80% of target is Poor, above 110% Exceed (see utils/chart.util).
const withProgress = (row) => {
  const percentage = row.recommended > 0 ? Math.round((row.value / row.recommended) * 100) : null;
  let status = "";
  if (percentage != null) {
    status = percentage < 80 ? "Poor" : percentage <= 110 ? "Correct" : "Exceed";
  }
  return { ...row, percentage, status };
};

// Consecutive tracked days ending on currentDate, within the fetched window.
const dailyStreak = (rows, currentDate) => {
  const tracked = new Set(rows.filter((r) => r.value > 0).map((r) => r.date));
  const day = parseLocalDate(currentDate);
  let streak = 0;
  while (tracked.has(formatLocalDate(day))) {
    streak += 1;
    day.setDate(day.getDate() - 1);
  }
  return streak;
};

const summarize = (rows, currentDate) => {
  const tracked = rows.filter((r) => r.value > 0);
  const mean = (pick) =>
    tracked.length ? round1(tracked.reduce((sum, r) => sum + (pick(r) || 0), 0) / tracked.length) : 0;
  return {
    data: rows,
    average_value: mean((r) => r.value),
    average_percentage: mean((r) => r.percentage),
    daily_streak: dailyStreak(rows, currentDate),
  };
};

/*Steps Summary */
export const fetchStepsSummary = async (get, profileId, range, currentDate, eventTypeId) => {
  try {
    const days = await fetchDailyTrackingSummary(
      get, "fetch-daily-step-tracking-summary", profileId, range, currentDate, eventTypeId
    );
    if (!days) {
      return { success: false, message: "Failed to fetch steps summary" };
    }

    const rows = days.map((d) =>
      withProgress({
        date: d.date,
        value: Math.round(d.total_actual_step_count || 0),
        recommended: Math.round(d.total_plan_step_count || 0),
      })
    );
    return { success: true, ...summarize(rows, currentDate) };
  } catch (error) {
    console.error("Error fetching steps summary:", error);
    return {
      success: false,
      message: "Error fetching steps summary",
    };
  }
};

/*Water Summary */
export const fetchWaterSummary = async (get, profileId, range, currentDate, eventTypeId) => {
  try {
    const days = await fetchDailyTrackingSummary(
      get, "fetch-daily-water-tracking-summary", profileId, range, currentDate, eventTypeId
    );
    if (!days) {
      return { success: false, message: "Failed to fetch water intake summary" };
    }

    // Water rows are split by quantity type; the card shows the day's total.
    const sum = (types, key) => (types || []).reduce((total, t) => total + (t[key] || 0), 0);
    const rows = days.map((d) =>
      withProgress({
        date: d.date,
        value: round1(sum(d.quantity_types, "total_actual_quantity")),
        recommended: round1(sum(d.quantity_types, "total_plan_water_quantity")),
      })
    );
    return { success: true, ...summarize(rows, currentDate) };
  } catch (error) {
    console.error("Error fetching water intake summary:", error);
    return {
      success: false,
      message: "Error fetching water intake summary",
    };
  }
};

/*Sleep Summary */
export const fetchSleepSummary = async (get, profileId, range, currentDate, eventTypeId) => {
  try {
    const days = await fetchDailyTrackingSummary(
      get, "fetch-daily-sleep-tracking-summary", profileId, range, currentDate, eventTypeId
    );
    if (!days) {
      return { success: false, message: "Failed to fetch sleep summary" };
    }

    const rows = days.map((d) =>
      withProgress({
        date: d.date,
        value: round1(d.total_actual_sleep_hours || 0),
        recommended: round1(d.total_plan_sleep_hours || 0),
      })
    );
    const { average_value, ...summary } = summarize(rows, currentDate);
    return {
      success: true,
      ...summary,
      average_duration_hours: average_value,
      average_duration_minutes: Math.round(average_value * 60),
    };
  } catch (error) {
    console.error("Error fetching sleep summary:", error);
    return {
      success: false,
      message: "Error fetching sleep summary",
    };
  }
};

/*Weight Summary */
export const fetchWeightSummary = async (get, profileId, range, currentDate) => {
  try {
    const url = `/weight/${profileId}/weight-summary?range=${range}&current_date=${currentDate}`;
    const response = await get(url);

    if (response && response.status === "success") {
      return {
        success: true,
        data: response.data,
        average_value: response.average_value,
        weight_change: response.weight_change,
        trend: response.trend,
      };
    }

    return {
      success: false,
      message: "Failed to fetch weight summary",
    };
  } catch (error) {
    console.error("Error fetching weight summary:", error);
    return {
      success: false,
      message: "Error fetching weight summary",
    };
  }
};
